import { useAtomValue, useSetAtom } from "jotai";
import { Pause, Timer } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { isInfoPacketPayload, isStatusPacketPayload } from "@/api/model/current-lic-packet";
import type { Device } from "@/api/model/device";
import Button from "@/components/ui/Button";
import { latestLICPacketByDeviceAndTypeAtom } from "@/store";
import { createDeviceMessageRequestAtom } from "@/store/crud";
import { Modal, useToast } from ".";

interface PauseCodekModalProps {
  /** Render-prop function that receives an open function to trigger the modal */
  trigger: (open: () => void) => React.ReactNode;
  /** Target LIC device to pause */
  device: Device;
  /** Optional callback for successful pause command */
  onSuccess?: () => void;
  /** Optional callback for pause command errors */
  onError?: (error: string) => void;
}

/**
 * Reusable modal component for pausing CODEK operations
 * Provides a warning message, duration input, and confirmation flow
 */
function PauseCodekModal({ trigger, device, onSuccess, onError }: PauseCodekModalProps) {
  const { showWarning, showSuccess } = useToast();
  const createDeviceMessageRequest = useSetAtom(createDeviceMessageRequestAtom);
  const latestLICPacketByDeviceAndType = useAtomValue(latestLICPacketByDeviceAndTypeAtom);
  const latestStatusPacket = useMemo(
    () => latestLICPacketByDeviceAndType(device.id, "status"),
    [device.id, latestLICPacketByDeviceAndType]
  );
  const statusData = useMemo(() => {
    if (!latestStatusPacket?.payload_data) return null;
    if (isStatusPacketPayload(latestStatusPacket.payload_data)) {
      return latestStatusPacket.payload_data.status;
    }
    return null;
  }, [latestStatusPacket]);

  /**
   * Determine if CODEK is currently paused based on status data
   */
  const isPaused = useMemo(() => {
    if (!statusData) return false;
    return !!statusData.scheduling_paused;
  }, [statusData]);

  // Internal state management
  const [isOpen, setIsOpen] = useState(false);
  const [pauseDuration, setPauseDuration] = useState<string>("30");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pauseDurationId = useId();

  const handleOpen = () => {
    setIsOpen(true);
    setPauseDuration("30");
    setErrorMessage(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setPauseDuration("30");
    setErrorMessage(null);
    setIsSubmitting(false);
  };

  const handleConfirmPause = async () => {
    const durationNum = parseInt(pauseDuration);
    if (isNaN(durationNum) || durationNum < 1) {
      setErrorMessage("Por favor, insira um valor válido em minutos");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const payload_data = isPaused
        ? {
            command: "resume",
          }
        : {
            command: "pause",
            durationMinutes: durationNum,
          };
      await createDeviceMessageRequest({
        device: device.id,
        payload_type: "command",
        payload_data,
      });

      showSuccess({
        message: `Comando de pausa enviado para ${device.identifier} por ${durationNum} minutos`,
      });

      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error sending pause command:", error);
      const errorMsg = "Falha ao enviar comando de pausa. Tente novamente.";
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      showWarning({
        message: errorMsg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {trigger(handleOpen)}

      <Modal isOpen={isOpen} onClose={handleClose} title="Pausar CODEK" size="md">
        <div className="space-y-4">
          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Pause className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800">Atenção</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Esta ação irá pausar as operações do CODEK e todos os agendamentos de irrigação pelo período
                  especificado. Use apenas quando necessário.
                </p>
              </div>
            </div>
          </div>

          {/* Duration Input */}
          <div className="space-y-2">
            <label htmlFor={pauseDurationId} className="block text-sm font-medium text-gray-700">
              Duração da Pausa (minutos)
            </label>
            <div className="relative">
              <Timer className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                id={pauseDurationId}
                type="number"
                min="1"
                value={pauseDuration}
                onChange={(e) => {
                  setPauseDuration(e.target.value);
                  setErrorMessage(null);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="30"
                disabled={isSubmitting}
              />
            </div>
            {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting} className="flex-1">
              Cancelar
            </Button>
            <Button
              variant={isPaused ? "primary" : "warning"}
              onClick={handleConfirmPause}
              disabled={isSubmitting || !pauseDuration || parseInt(pauseDuration) < 1}
              className="flex-1"
              icon={<Pause className="h-4 w-4" />}
            >
              {isSubmitting ? "Enviando..." : isPaused ? "Retomar" : "Confirmar Pausa"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default PauseCodekModal;
